import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BRAND_ID as STORE_ID, MECRA_PLANI as STORE_MECRA,
  TOPLAM_BUTCE as STORE_TOPLAM, SILO_PLANI as STORE_SILO,
} from './HupalupaStoreEylul2026MedyaPlani';
import { BRAND_ID as EGLENCE_ID, MECRA_PLANI as EGLENCE_MECRA } from './HupalupaEglenceEylul2026MedyaPlani';
import { BRAND_ID as KIXX_ID, MECRA_PLANI as KIXX_MECRA } from './KixxEylul2026MedyaPlani';
import { BRAND_ID as KERAMET_ID, MECRA_PLANI as KERAMET_MECRA } from './KerametIlicasiEylul2026MedyaPlani';

/* ═══════════════════════════════════════════════════════════════════════════
   HUPALUPA GRUBU — EYLÜL 2026 PLAN DURUMU              /budget/hupalupa-eylul-2026-durum
   ---------------------------------------------------------------------------
   Dört medya planının TEK ekranda "plan be plan nerede duruyoruz" cevabı.
   Planların kendisi ayrı sayfalarda duruyor; bu sayfa onların yerine geçmez,
   üstlerinde durur ve her satırdan kendi planına link verir.

   🔑🔑 Plan rakamları BURADA YAZILI DEĞİL — dört plan sayfasından import
   ediliyor (`MECRA_PLANI`, `TOPLAM_BUTCE`, `SILO_PLANI`). Bir planın bütçesi
   değişirse bu sayfa kendiliğinden düzelir; iki yerde ayrı rakam tutulmuyor.

   🔑 Gerçekleşen tarafı canlı: `/api/group/hupalupa/gerceklesen` dört markayı
   birden, kampanya seviyesinde okur. Aynı uç plan sayfalarının içindeki
   `EylulGerceklesen` bölümünü de besliyor — iki ekran aynı rakamı gösterir.

   🔴 Varsayılan aralık ayın 1'i → DÜN. Bugün yarım gündür; kümülatif eğri ve
   ay sonu projeksiyonu yarım günü gerçek bir yavaşlama gibi gösterirdi.

   🔴 Grafikteki dört renk doğrulanmış kategorik paletin ilk dört slotu
   (scripts/validate_palette.js: tüm denetimler PASS, kontrast uyarısı
   doğrudan etiketle karşılanıyor). Marka kimlik renkleri BİLEREK
   kullanılmadı: Kixx'in kimliği neredeyse siyah, çizgi olarak okunmuyor.
   ═══════════════════════════════════════════════════════════════════════════ */

const API_BASE = (() => {
  const raw = (process.env.REACT_APP_API_URL || 'https://api.glowytics.com').trim();
  return raw.replace(/\/+$/, '').replace(/\/api$/, '');
})();

const MECRA = {
  GOOGLE_ADS: { ad: 'Google Ads', logo: '/google_ads-512.webp' },
  META_ADS: { ad: 'Meta Ads', logo: '/6033716.png' },
  TIKTOK_ADS: { ad: 'TikTok Ads', logo: '/tiktokv4.svg' },
};
const MECRA_SIRA = ['GOOGLE_ADS', 'META_ADS', 'TIKTOK_ADS'];

const AY_ADLARI = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
];

/* Plan tanımları — bütçeler dört plan sayfasından geliyor, burada yalnızca
   kimlik ve rota var. `renk` doğrulanmış kategorik paletin sırası. */
const PLANLAR = [
  {
    id: STORE_ID, ad: 'HUPALUPA Store', kisa: 'Store',
    rota: '/budget/hupalupa-store-eylul-2026-medya-plani',
    mecra: STORE_MECRA, toplam: STORE_TOPLAM, silolar: STORE_SILO,
    kpi: 'revenue', renk: '#2a78d6',
    not: 'Store geneli + altı marka silosu ayrı bütçeli',
  },
  {
    id: EGLENCE_ID, ad: 'HUPALUPA Eğlence', kisa: 'Eğlence',
    rota: '/budget/hupalupa-eglence-eylul-2026-medya-plani',
    mecra: EGLENCE_MECRA, kpi: 'conversation', renk: '#eb6834',
    not: 'Ağustos kapanışına sabitlendi, atölye ve doğum günü öne alındı',
  },
  {
    id: KIXX_ID, ad: 'Kixx Sports Club', kisa: 'Kixx',
    rota: '/budget/kixx-eylul-2026-medya-plani',
    mecra: KIXX_MECRA, kpi: 'lead', renk: '#1baf7a',
    not: 'Ağırlık Meta’dan Google’a kaydırıldı',
  },
  {
    id: KERAMET_ID, ad: 'Keramet Ilıcası', kisa: 'Keramet',
    rota: '/budget/keramet-ilicasi-eylul-2026-medya-plani',
    mecra: KERAMET_MECRA, kpi: 'reach', renk: '#eda100',
    not: 'Toplam düşürüldü, Google payı büyütüldü',
  },
];

const planToplami = (p) =>
  p.toplam != null ? p.toplam : Object.values(p.mecra).reduce((a, b) => a + (b || 0), 0);

const GRUP_TOPLAM = PLANLAR.reduce((a, p) => a + planToplami(p), 0);

/* ── Biçimleyiciler ─────────────────────────────────────────────────────── */
const tl = (v) => `₺${Math.round(Number(v) || 0).toLocaleString('tr-TR')}`;
const kurus = (v) =>
  `₺${(Number(v) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const say = (v) => (Number(v) || 0).toLocaleString('tr-TR');
const yuzde = (v, basamak = 0) =>
  v == null || !Number.isFinite(v) ? '—' : `%${(v * 100).toFixed(basamak).replace('.', ',')}`;

/* Türkçe iyelik eki: "planın %21,1'i" ama "planın %26,7'si". Ek son RAKAMIN
   okunuşuna bağlı; elle yazılınca hep yanlış çıkıyor. Onluklarda (10, 20, …)
   ek onluğun okunuşundan gelir. */
const TR_EK = {
  0: 'ı', 1: 'i', 2: 'si', 3: 'ü', 4: 'dü', 5: 'i', 6: 'sı', 7: 'si', 8: 'i', 9: 'u',
  10: 'u', 20: 'si', 30: 'u', 40: 'ı', 50: 'si', 60: 'ı', 70: 'i', 80: 'i', 90: 'ı', 100: 'ü',
};
function ek(metin) {
  const rakamlar = String(metin).replace(/[^0-9]/g, '');
  if (!rakamlar) return '';
  const son = Number(rakamlar[rakamlar.length - 1]);
  if (son !== 0) return TR_EK[son];
  const onluk = Number(rakamlar.slice(-2)) || 100;
  return TR_EK[onluk] || 'ı';
}
/** "planın %21,1'i" gibi tam ifade. */
const yuzdeEkli = (v, basamak = 0) => {
  const y = yuzde(v, basamak);
  return y === '—' ? '—' : `${y}'${ek(y)}`;
};

function gunEtiketi(iso) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(iso || ''))) return iso || '';
  const [, ay, gun] = iso.split('-');
  return `${Number(gun)} ${AY_ADLARI[Number(ay) - 1]}`;
}

function bugunIso() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/* Eşik ±%10: bunun altındaki sapmayı hafta sonu / onay bekleyen kreatif
   kapatıyor, renk vermeye değmiyor. */
function durum(gercek, beklenen) {
  if (!beklenen) return gercek > 0 ? 'fazla' : 'yok';
  const o = gercek / beklenen;
  if (o < 0.9) return 'geri';
  if (o > 1.1) return 'fazla';
  return 'uyumlu';
}
const DURUM_STIL = {
  uyumlu: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  geri: 'bg-amber-50 text-amber-700 border-amber-200',
  fazla: 'bg-rose-50 text-rose-700 border-rose-200',
  yok: 'bg-gray-50 text-gray-500 border-gray-200',
};
const DURUM_METNI = {
  uyumlu: 'gün oranında',
  geri: 'gün oranının altında',
  fazla: 'gün oranının üstünde',
  yok: 'harcama yok',
};
function Rozet({ tip }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[11px] font-medium whitespace-nowrap ${DURUM_STIL[tip]}`}>
      {DURUM_METNI[tip]}
    </span>
  );
}

/* ── Kümülatif eğri ──────────────────────────────────────────────────────
   Dört markanın bütçesi 20 kat farklı (₺70K ↔ ₺1,31M); mutlak lira aynı
   eksende çizilirse üç marka taban çizgisine yapışır. Bu yüzden ortak tabana
   İNDEKSLENİYOR: her nokta "ay planının yüzde kaçı yandı". Kesikli köşegen
   plan hızıdır — bütçe her güne eşit dağıtılsaydı izlenecek yol.
   🔑 İki y-ekseni YOK; tek ölçü, tek eksen. */

const C = { sol: 44, sag: 132, ust: 16, alt: 30, g: 900, y: 300 };

function Egri({ markalar, aralik }) {
  const [vurgu, setVurgu] = useState(null);
  const svgRef = useRef(null);

  const gunler = aralik?.ayGun || 30;
  const seri = useMemo(() => markalar.filter((m) => m.gunluk?.length), [markalar]);

  // Y ekseni: en yüksek kümülatif oran ile plan hızının sonu, hangisi büyükse.
  const tavan = useMemo(() => {
    const enYuksek = Math.max(
      ...seri.map((m) => Math.max(...m.gunluk.map((d) => d.oran), 0)),
      aralik ? aralik.gun / gunler : 0
    );
    return Math.max(0.1, Math.ceil((enYuksek * 1.15) * 20) / 20);
  }, [seri, aralik, gunler]);

  const x = (gun) => C.sol + ((gun - 1) / (gunler - 1)) * (C.g - C.sol - C.sag);
  const y = (oran) => C.ust + (1 - Math.min(oran, tavan) / tavan) * (C.y - C.ust - C.alt);

  const isaretler = useMemo(() => {
    const adim = tavan <= 0.2 ? 0.05 : tavan <= 0.5 ? 0.1 : 0.25;
    const out = [];
    for (let v = 0; v <= tavan + 1e-9; v += adim) out.push(Math.round(v * 1000) / 1000);
    return out;
  }, [tavan]);

  /* Doğrudan etiketler çakışıyordu: dört çizginin ucu birkaç puan aralıkta
     bitince "Store %22,7" ile "Kixx %20,1" üst üste biniyordu. Etiketler
     çizginin ucundan AŞAĞI doğru itilerek en az ETIKET_ARA piksel arayla
     yerleştiriliyor; kaydırılan etiket kendi çizgisine ince bir bağla
     bağlanıyor ki hangi seriye ait olduğu belirsiz kalmasın. */
  const etiketler = useMemo(() => {
    const ETIKET_ARA = 26;
    const liste = seri
      .map((m) => {
        const son = m.gunluk[m.gunluk.length - 1];
        return { m, son, hedef: y(son.oran) };
      })
      .sort((a, b) => a.hedef - b.hedef);
    let onceki = -Infinity;
    for (const e of liste) {
      e.etiketY = Math.max(e.hedef + 3.5, onceki + ETIKET_ARA);
      onceki = e.etiketY;
    }
    // Alttan taşarsa bloğun tamamını yukarı kaydır.
    const tasma = (liste[liste.length - 1]?.etiketY || 0) - (C.y - C.alt);
    if (tasma > 0) for (const e of liste) e.etiketY -= tasma;
    return liste;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seri, tavan, gunler]);

  const konum = (e) => {
    const svg = svgRef.current;
    if (!svg) return;
    const kutu = svg.getBoundingClientRect();
    const oran = (e.clientX - kutu.left) / kutu.width;
    const px = oran * C.g;
    const gun = Math.round(((px - C.sol) / (C.g - C.sol - C.sag)) * (gunler - 1)) + 1;
    const enFazla = seri[0]?.gunluk.length || 0;
    setVurgu(gun >= 1 && gun <= enFazla ? gun : null);
  };

  if (!seri.length) return null;

  return (
    <div>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${C.g} ${C.y}`}
        className="w-full h-auto"
        onMouseMove={konum}
        onMouseLeave={() => setVurgu(null)}
      >
        {/* Izgara — geri planda, veriyle yarışmıyor */}
        {isaretler.map((v) => (
          <g key={v}>
            <line x1={C.sol} x2={C.g - C.sag} y1={y(v)} y2={y(v)} stroke="#e9edf2" strokeWidth="1" />
            <text x={C.sol - 8} y={y(v) + 3.5} textAnchor="end" fontSize="10" fill="#8b95a1">
              {yuzde(v)}
            </text>
          </g>
        ))}

        {/* Gün ekseni: 1, 5, 10 … ve ayın son günü */}
        {Array.from({ length: gunler }, (_, i) => i + 1)
          .filter((g) => g === 1 || g === gunler || g % 5 === 0)
          .map((g) => (
            <text key={g} x={x(g)} y={C.y - 10} textAnchor="middle" fontSize="10" fill="#8b95a1">
              {g}
            </text>
          ))}

        {/* Plan hızı — kesikli köşegen */}
        <line
          x1={x(1)} y1={y(1 / gunler)} x2={x(gunler)} y2={y(1)}
          stroke="#9aa5b1" strokeWidth="1.5" strokeDasharray="5 4"
        />
        <text x={x(gunler) + 6} y={y(1) + 3.5} fontSize="10" fill="#7b8794">plan hızı</text>

        {/* Marka eğrileri + çakışmayan doğrudan etiketler */}
        {etiketler.map(({ m, son, etiketY }) => {
          const d = m.gunluk.map((p, i) => `${i ? 'L' : 'M'}${x(p.gun)},${y(p.oran)}`).join(' ');
          const uc = y(son.oran);
          return (
            <g key={m.id}>
              <path d={d} fill="none" stroke={m.renk} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
              {/* Uçta 2px yüzey halkalı işaretçi — çakışan çizgiler ayrışsın */}
              <circle cx={x(son.gun)} cy={uc} r="4" fill={m.renk} stroke="#ffffff" strokeWidth="2" />
              {/* Etiket kaydırıldıysa hangi çizginin etiketi olduğu bağ çizgisiyle belli olsun */}
              {Math.abs(etiketY - uc) > 2 && (
                <path
                  d={`M${x(son.gun) + 5},${uc} L${x(son.gun) + 11},${etiketY - 3}`}
                  stroke={m.renk} strokeWidth="1" fill="none" opacity="0.5"
                />
              )}
              {/* Doğrudan etiket: kimlik renkle DEĞİL yazıyla taşınıyor */}
              <text x={x(son.gun) + 13} y={etiketY} fontSize="11" fontWeight="600" fill="#3e4c59">
                {m.kisa}
              </text>
              <text x={x(son.gun) + 13} y={etiketY + 12} fontSize="10" fill="#8b95a1">
                {yuzde(son.oran, 1)}
              </text>
            </g>
          );
        })}

        {/* İmleç */}
        {vurgu && (
          <g>
            <line x1={x(vurgu)} x2={x(vurgu)} y1={C.ust} y2={C.y - C.alt} stroke="#3e4c59" strokeWidth="1" strokeDasharray="3 3" />
            {seri.map((m) => {
              const p = m.gunluk[vurgu - 1];
              if (!p) return null;
              return <circle key={m.id} cx={x(vurgu)} cy={y(p.oran)} r="4" fill={m.renk} stroke="#ffffff" strokeWidth="2" />;
            })}
          </g>
        )}
      </svg>

      {/* Okuma satırı — imleçsizken son günü yazar */}
      <div className="mt-1 flex flex-wrap items-center gap-x-5 gap-y-1 px-1 text-[11px]">
        <span className="font-medium text-gray-700">
          {vurgu ? `Ayın ${vurgu}. günü` : `Ayın ${aralik?.gun}. günü sonunda`}
        </span>
        {seri.map((m) => {
          const p = m.gunluk[(vurgu || m.gunluk.length) - 1];
          if (!p) return null;
          return (
            <span key={m.id} className="inline-flex items-center gap-1.5 text-gray-600">
              <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: m.renk }} />
              {m.kisa}
              <strong className="text-gray-800">{yuzde(p.oran, 1)}</strong>
              <span className="text-gray-400">{tl(p.kumulatif)}</span>
            </span>
          );
        })}
        <span className="inline-flex items-center gap-1.5 text-gray-500">
          <span className="inline-block w-4 border-t border-dashed border-gray-400" />
          plan hızı <strong className="text-gray-700">{yuzde((vurgu || aralik?.gun || 0) / (aralik?.ayGun || 30), 1)}</strong>
        </span>
      </div>
    </div>
  );
}

/* ── Sayfa ───────────────────────────────────────────────────────────────── */

export default function HupalupaEylulDurum() {
  const [veri, setVeri] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState(null);
  const [bugunKat, setBugunKat] = useState(false);
  const [acik, setAcik] = useState(null);

  const getir = useCallback(async (yenile = false) => {
    setYukleniyor(true);
    setHata(null);
    try {
      const qs = new URLSearchParams();
      if (bugunKat) qs.set('until', bugunIso());
      if (yenile) qs.set('refresh', '1');
      const res = await fetch(`${API_BASE}/api/group/hupalupa/gerceklesen?${qs}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        credentials: 'include',
      });
      const json = await res.json();
      if (!res.ok || !json.ok) throw new Error(json.error || `HTTP ${res.status}`);
      setVeri(json);
    } catch (e) {
      setHata(e.message);
    } finally {
      setYukleniyor(false);
    }
  }, [bugunKat]);

  useEffect(() => { getir(false); }, [getir]);

  const aralik = veri?.range || null;
  const oran = aralik?.oran || null;

  /* Plan tanımı ile canlı veriyi tek satırda birleştir. */
  const satirlar = useMemo(() => {
    if (!veri) return [];
    return PLANLAR.map((p) => {
      const canli = veri.brands.find((b) => b.id === p.id) || null;
      const plan = planToplami(p);
      const gercek = canli?.totals?.spend || 0;
      const gunAnahtarlari = Object.keys(canli?.daily || {}).sort();
      let kum = 0;
      const gunluk = gunAnahtarlari.map((g) => {
        kum += canli.daily[g].spend;
        return { gun: Number(g.slice(8)), kumulatif: kum, oran: plan ? kum / plan : 0 };
      });
      return {
        ...p, canli, plan, gercek, gunluk,
        beklenen: oran ? plan * oran : null,
        aySonu: oran ? gercek / oran : null,
      };
    });
  }, [veri, oran]);

  const grup = useMemo(() => {
    const gercek = satirlar.reduce((a, s) => a + s.gercek, 0);
    return {
      plan: GRUP_TOPLAM,
      gercek,
      beklenen: oran ? GRUP_TOPLAM * oran : null,
      aySonu: oran ? gercek / oran : null,
    };
  }, [satirlar, oran]);

  const storeSatir = satirlar.find((s) => s.silolar);

  /* Store'un marka siloları: harcama kampanya ADINDAN çözülür, hiçbir desene
     uymayan kampanya `varsayilan` siloya (Store geneli) düşer. */
  const silolar = useMemo(() => {
    if (!storeSatir?.canli) return null;
    const kova = storeSatir.silolar.map((s) => ({ ...s, gercek: 0, kampanya: 0 }));
    const varsayilan = kova.find((s) => s.varsayilan) || null;
    for (const k of storeSatir.canli.campaigns) {
      const hit = kova.find((s) => !s.varsayilan && s.desen && s.desen.test(k.name));
      const hedef = hit || varsayilan;
      if (!hedef) continue;
      hedef.gercek += k.spend;
      hedef.kampanya += 1;
    }
    return kova;
  }, [storeSatir]);

  const hatalar = (veri?.brands || []).flatMap((b) => (b.errors || []).map((e) => `${b.label}: ${e.account} (${e.error})`));

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">

      {/* ── Başlık ── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="border-b p-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-800">HUPALUPA Grubu — Eylül 2026 plan durumu</h2>
            <p className="text-sm text-gray-500">
              Dört medya planı, tek ekranda: bütçenin ne kadarı yandı, hangi hızla gidiyor, ay sonu nereye çıkar.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center px-3 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full border border-gray-200">
              {aralik ? `${gunEtiketi(aralik.since)} - ${gunEtiketi(aralik.until)}` : 'Eylül 2026'}
            </span>
            <button
              type="button"
              onClick={() => setBugunKat((v) => !v)}
              className={`px-2.5 py-1 rounded-md border text-[11px] font-medium ${
                bugunKat ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-700 border-gray-300'
              }`}
            >
              Bugünü de kat
            </button>
            <button
              type="button"
              onClick={() => getir(true)}
              disabled={yukleniyor}
              className="px-2.5 py-1 rounded-md border border-gray-300 bg-white text-[11px] font-medium text-gray-700 disabled:opacity-50"
            >
              {yukleniyor ? 'Okunuyor...' : 'Yenile'}
            </button>
          </div>
        </div>

        {hata && (
          <div className="px-5 py-3 bg-rose-50 border-b border-rose-200 text-[12px] text-rose-800">
            Gerçekleşen veri okunamadı: {hata}
          </div>
        )}

        {!veri && yukleniyor && (
          <div className="px-5 py-12 text-center text-[12px] text-gray-500">
            Dört markanın Google, Meta ve TikTok hesapları okunuyor. İlk okuma yaklaşık 20 saniye sürer.
          </div>
        )}

        {veri && (
          <>
            {/* Grup şeridi */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 p-5">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <p className="text-[10px] font-medium text-gray-600">Dört planın toplamı</p>
                <p className="text-xl font-bold text-gray-800 mt-0.5">{tl(grup.plan)}</p>
                <p className="text-[10px] text-gray-500">Eylül ayı bütçesi</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white p-3">
                <p className="text-[10px] font-medium text-gray-600">Bugüne kadar harcanan</p>
                <p className="text-xl font-bold text-gray-900 mt-0.5">{tl(grup.gercek)}</p>
                <p className="text-[10px] text-gray-500">
                  ayın {aralik.gun}. günü · planın {yuzdeEkli(grup.gercek / grup.plan, 1)}
                </p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white p-3">
                <p className="text-[10px] font-medium text-gray-600">Gün oranına göre beklenen</p>
                <p className="text-xl font-bold text-gray-800 mt-0.5">{tl(grup.beklenen)}</p>
                <p className="text-[10px] text-gray-500">planın {yuzdeEkli(oran, 1)}</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white p-3">
                <p className="text-[10px] font-medium text-gray-600">Bu hızla ay sonu</p>
                <p className="text-xl font-bold text-gray-800 mt-0.5">{tl(grup.aySonu)}</p>
                <p className="text-[10px] text-gray-500">
                  plana göre {grup.aySonu >= grup.plan ? '+' : '−'}{tl(Math.abs(grup.aySonu - grup.plan))}
                </p>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Kümülatif eğri ── */}
      {veri && (
        <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
          <div className="bg-slate-100 px-4 py-3">
            <p className="font-semibold text-gray-800">Plan hızına göre nerede duruyoruz</p>
            <p className="text-[11px] text-gray-600 mt-0.5">
              Her çizgi, o planın ay bütçesinin yüzde kaçının yandığını gösterir. Dört bütçe 20 kat
              farklı olduğu için lira değil ORAN çizildi; kesikli köşegen bütçenin her güne eşit
              dağıtılması hâlinde izlenecek yoldur. Çizginin altında kalmak geride, üstünde olmak
              öndedir.
            </p>
          </div>
          <div className="p-4">
            <Egri markalar={satirlar} aralik={aralik} />
          </div>
        </div>
      )}

      {/* ── Plan plan tablo ── */}
      {veri && (
        <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
          <div className="bg-slate-100 px-4 py-3 font-semibold text-gray-800">Plan plan durum</div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2.5 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                  <th className="px-4 py-2.5 text-right text-[11px] font-medium text-gray-500 uppercase tracking-wider">Ay bütçesi</th>
                  <th className="px-4 py-2.5 text-right text-[11px] font-medium text-gray-500 uppercase tracking-wider">Harcanan</th>
                  <th className="px-4 py-2.5 text-right text-[11px] font-medium text-gray-500 uppercase tracking-wider">Beklenen</th>
                  <th className="px-4 py-2.5 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider">Plana göre</th>
                  <th className="px-4 py-2.5 text-right text-[11px] font-medium text-gray-500 uppercase tracking-wider">Bu hızla ay sonu</th>
                  <th className="px-4 py-2.5 text-right text-[11px] font-medium text-gray-500 uppercase tracking-wider">Sonuç</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100 text-[12px] text-gray-700">
                {satirlar.map((s) => {
                  const t = s.canli?.totals;
                  const acikMi = acik === s.id;
                  return (
                    <React.Fragment key={s.id}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-start gap-2.5">
                            <span className="mt-1 inline-block w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: s.renk }} />
                            <div>
                              <a href={s.rota} className="font-semibold text-gray-900 hover:text-blue-700 hover:underline">
                                {s.ad}
                              </a>
                              <p className="text-[10px] text-gray-500 mt-0.5">{s.not}</p>
                              <button
                                type="button"
                                onClick={() => setAcik(acikMi ? null : s.id)}
                                className="mt-1 text-[10px] font-medium text-blue-700 hover:underline"
                              >
                                {acikMi ? 'Mecra kırılımını gizle' : 'Mecra kırılımını göster'}
                              </button>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">{tl(s.plan)}</td>
                        <td className="px-4 py-3 text-right font-semibold text-gray-900">{tl(s.gercek)}</td>
                        <td className="px-4 py-3 text-right text-gray-500">{s.beklenen ? tl(s.beklenen) : '—'}</td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-1">
                            <Rozet tip={durum(s.gercek, s.beklenen)} />
                            <span className="text-[10px] text-gray-500">
                              gün oranına göre {yuzde(s.beklenen ? s.gercek / s.beklenen : null)}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">{s.aySonu ? tl(s.aySonu) : '—'}</td>
                        <td className="px-4 py-3 text-right">
                          {t ? (
                            <div className="text-[11px] leading-relaxed">
                              {s.kpi === 'revenue' && <div>{say(t.purchases)} satış</div>}
                              {s.kpi === 'lead' && <div>{say(t.leads)} form</div>}
                              {s.kpi === 'conversation' && <div>{say(t.conversations)} mesaj</div>}
                              {s.kpi === 'reach' && <div>{say(t.impressions)} gösterim</div>}
                              <div className="text-gray-400">
                                {s.kpi === 'reach'
                                  ? `${say(t.views)} izlenme`
                                  : `${t.clicks ? kurus(t.spend / t.clicks) : '—'} CPC`}
                              </div>
                            </div>
                          ) : '—'}
                        </td>
                      </tr>

                      {acikMi && s.canli && (
                        <tr>
                          <td colSpan={7} className="px-4 py-0 bg-slate-50">
                            <div className="py-3">
                              <table className="min-w-full text-[11px]">
                                <thead>
                                  <tr className="text-gray-500">
                                    <th className="px-3 py-1.5 text-left font-medium">Mecra</th>
                                    <th className="px-3 py-1.5 text-right font-medium">Plan</th>
                                    <th className="px-3 py-1.5 text-right font-medium">Harcanan</th>
                                    <th className="px-3 py-1.5 text-right font-medium">Beklenen</th>
                                    <th className="px-3 py-1.5 text-left font-medium">Plana göre</th>
                                    <th className="px-3 py-1.5 text-right font-medium">Bu hızla ay sonu</th>
                                  </tr>
                                </thead>
                                <tbody className="text-gray-700">
                                  {MECRA_SIRA.filter((m) => s.mecra[m] != null || s.canli.channels[m]).map((m) => {
                                    const p = s.mecra[m] || 0;
                                    const g = s.canli.channels[m]?.spend || 0;
                                    const bek = oran ? p * oran : null;
                                    return (
                                      <tr key={m} className="border-t border-slate-200">
                                        <td className="px-3 py-1.5">
                                          <span className="inline-flex items-center gap-1.5">
                                            <img src={MECRA[m].logo} alt={MECRA[m].ad} className="w-3.5 h-3.5 object-contain" />
                                            {MECRA[m].ad}
                                            <span className="text-gray-400">
                                              {s.canli.channels[m]?.campaigns || 0} kampanya
                                            </span>
                                          </span>
                                        </td>
                                        <td className="px-3 py-1.5 text-right">{p ? tl(p) : '—'}</td>
                                        <td className="px-3 py-1.5 text-right font-medium">{tl(g)}</td>
                                        <td className="px-3 py-1.5 text-right text-gray-500">{bek ? tl(bek) : '—'}</td>
                                        <td className="px-3 py-1.5"><Rozet tip={durum(g, bek)} /></td>
                                        <td className="px-3 py-1.5 text-right">{oran ? tl(g / oran) : '—'}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                              <p className="px-3 pt-2 text-[10px] text-gray-500">
                                Kampanya kampanya döküm planın kendi sayfasında:{' '}
                                <a href={s.rota} className="text-blue-700 hover:underline">{s.ad} medya planı</a>
                              </p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                <tr className="bg-gray-800 text-white font-bold">
                  <td className="px-4 py-3">GRUP TOPLAMI</td>
                  <td className="px-4 py-3 text-right">{tl(grup.plan)}</td>
                  <td className="px-4 py-3 text-right">{tl(grup.gercek)}</td>
                  <td className="px-4 py-3 text-right">{grup.beklenen ? tl(grup.beklenen) : '—'}</td>
                  <td className="px-4 py-3 text-[11px]">
                    gün oranına göre {yuzde(grup.beklenen ? grup.gercek / grup.beklenen : null)}
                  </td>
                  <td className="px-4 py-3 text-right">{grup.aySonu ? tl(grup.aySonu) : '—'}</td>
                  <td className="px-4 py-3" />
                </tr>
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2.5 bg-slate-50 border-t border-slate-200 text-[11px] text-gray-700 leading-relaxed">
            "Beklenen", ay bütçesinin geçen gün sayısına düşen payıdır; bütçe her güne eşit
            dağıtılsaydı bugüne kadar yanmış olması gereken tutar. "Bu hızla ay sonu" aynı hızın ayın
            tamamına uzatılmasıdır, taahhüt değildir. Sonuç sütunu her markanın kendi KPI'ıdır: Store
            satış, Eğlence mesaj, Kixx form, Keramet erişim. Ortak bir sonuç sütunu üç markada sıfır yazardı.
          </div>
        </div>
      )}

      {/* ── Store'un marka siloları ── */}
      {silolar && (
        <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
          <div className="bg-slate-100 px-4 py-3">
            <p className="font-semibold text-gray-800">Store planının içi — marka silosu bazında</p>
            <p className="text-[11px] text-gray-600 mt-0.5">
              Store'un bütçesi tek kalem değil: mağazanın içindeki altı markanın bütçesi ayrı yönetiliyor.
              Harcama kampanya adından ayrıştırıldı; hiçbir markaya ait olmayan kampanyalar Store geneline yazıldı.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2.5 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider">Kalem</th>
                  <th className="px-4 py-2.5 text-right text-[11px] font-medium text-gray-500 uppercase tracking-wider">Ay bütçesi</th>
                  <th className="px-4 py-2.5 text-right text-[11px] font-medium text-gray-500 uppercase tracking-wider">Harcanan</th>
                  <th className="px-4 py-2.5 text-right text-[11px] font-medium text-gray-500 uppercase tracking-wider">Beklenen</th>
                  <th className="px-4 py-2.5 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider">Plana göre</th>
                  <th className="px-4 py-2.5 text-right text-[11px] font-medium text-gray-500 uppercase tracking-wider">Bu hızla ay sonu</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100 text-[12px] text-gray-700">
                {silolar.map((s) => {
                  const bek = oran ? s.butce * oran : null;
                  return (
                    <tr key={s.etiket} className="hover:bg-gray-50">
                      <td className="px-4 py-2.5">
                        <span className="font-medium">{s.etiket}</span>
                        <span className="ml-2 text-[10px] text-gray-400">{s.kampanya} kampanya</span>
                      </td>
                      <td className="px-4 py-2.5 text-right">{tl(s.butce)}</td>
                      <td className="px-4 py-2.5 text-right font-semibold text-gray-900">{tl(s.gercek)}</td>
                      <td className="px-4 py-2.5 text-right text-gray-500">{bek ? tl(bek) : '—'}</td>
                      <td className="px-4 py-2.5"><Rozet tip={durum(s.gercek, bek)} /></td>
                      <td className="px-4 py-2.5 text-right">{oran ? tl(s.gercek / oran) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Okuma notları ── */}
      {veri && (
        <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
          <div className="bg-slate-100 px-4 py-3 font-semibold text-gray-800">Rakamları okurken</div>
          <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-5 text-[12px] text-gray-700">
            <div>
              <p className="font-semibold text-gray-800 mb-1">Veri canlı, plan dondurulmuş</p>
              <p className="leading-relaxed">
                Harcama Google Ads, Meta ve TikTok hesaplarından kampanya seviyesinde her açılışta
                yeniden okunur, hiçbir yere yazılmaz. Plan tarafı dört plan sayfasının kendi
                rakamlarıdır; bir planın bütçesi değişirse bu sayfa kendiliğinden düzelir.
              </p>
            </div>
            <div>
              <p className="font-semibold text-gray-800 mb-1">Bugün sayılmıyor</p>
              <p className="leading-relaxed">
                Aralık ayın 1'inden düne kadar. Yarım gün, ay sonu projeksiyonunu gerçek bir
                yavaşlama gibi aşağı çeker. Bugünü görmek için üstteki düğme.
              </p>
            </div>
            <div>
              <p className="font-semibold text-gray-800 mb-1">TikTok'ta paylaşılan hesap</p>
              <p className="leading-relaxed">
                Store'un NeeDoh kampanyaları Eğlence ile aynı TikTok hesabında duruyor. İki markanın
                payı kampanya adına göre ayrıştırılır; aynı harcama iki kez sayılmaz ve Store'un
                payı kaybolmaz.
              </p>
            </div>
            <div>
              <p className="font-semibold text-gray-800 mb-1">Ciro burada yok</p>
              <p className="leading-relaxed">
                Mecraların beyan ettiği ciro çift sayımlıdır: aynı satışı Google da Meta da kendine
                yazar. Grup toplamında ciro göstermek yanıltıcı olurdu; satış adedi Store satırında,
                tek sayımlı ciro GA4 tarafındadır.
              </p>
            </div>
          </div>
          {hatalar.length > 0 && (
            <div className="px-4 py-2.5 bg-amber-50 border-t border-amber-200 text-[11px] text-amber-800">
              Uyarı: {hatalar.join(' · ')} okunamadı; toplamlar bu hesaplar olmadan hesaplandı.
            </div>
          )}
          {veri.fetchedAt && (
            <div className="px-4 py-2 bg-slate-50 border-t border-slate-200 text-[10px] text-gray-500">
              Son okuma {new Date(veri.fetchedAt).toLocaleString('tr-TR')}
              {veri.cached ? ' (önbellekten, en fazla 10 dakikalık)' : ''}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
